import { ComponentProps, MouseEvent, useCallback, useMemo, useState } from 'react';
import pick from 'lodash/pick';
import { Button, Menu, MenuItem, styled, Typography } from '@mui/material';
import { PartialBy } from '@global/types';
import {
    conditionTitles,
    IArgumentMetadata,
    IPolicyCondition,
    IPolicyConditionWithArgMetadata,
    isBaseCondition,
    isCategoricalMetadata,
    Predicate,
} from '@tymely/atoms';
import { newUiId } from '@tymely/utils';

import { ConditionEditorProps } from './BaseConditionEditor';

type PR = Predicate | 'dont_care';
type CT = typeof conditionTitles;

const ButtonLikeTypography = styled(Typography, {
    shouldForwardProp: (prop) =>
        !['buttonVariant', 'disabled', 'fullWidth', 'hasRightNeighbors'].includes(String(prop)),
})<{
    buttonVariant?: ComponentProps<typeof Button>['variant'];
    disabled?: boolean;
    fullWidth?: boolean;
    hasRightNeighbors?: boolean;
}>(({ buttonVariant, disabled, fullWidth, hasRightNeighbors, theme }) => ({
    display: 'flex',
    whiteSpace: 'nowrap',
    height: '100%',
    borderRadius: hasRightNeighbors ? '10px 4px 4px 10px' : '10px',
    alignItems: 'center',
    justifyContent: 'center',
    ...(fullWidth ? { width: '100%' } : {}),
    paddingLeft: '8px',
    paddingRight: '8px',
    '&:hover': {
        ...(!disabled && { cursor: 'pointer' }),
    },
    ...(disabled
        ? { color: theme.palette.text.disabled }
        : {
              ...(buttonVariant === 'outlined' && {
                  color: theme.palette.text.secondary,
                  '&:hover': {
                      color: theme.palette.grey.A700,
                      backgroundColor: theme.palette.grey[200],
                      cursor: 'pointer',
                  },
              }),
              ...(buttonVariant === 'contained' && {
                  color: theme.palette.info.contrastText,
                  '&:hover': {
                      cursor: 'pointer',
                  },
              }),
              ...(buttonVariant === 'text' && {
                  '&:hover': {
                      color: theme.palette.info.contrastText,
                      backgroundColor: theme.palette.grey[200],
                      cursor: 'pointer',
                  },
              }),
          }),
}));

function getPredicates(argMetadata: IArgumentMetadata): Partial<CT> {
    let predicates: Partial<CT>;

    if (argMetadata.is_list) {
        predicates = pick<CT, keyof CT>(
            conditionTitles,
            'dont_care',
            'missing',
            'length_equals',
            'length_not_equals',
            'longer_than',
            'shorter_than',
            'is_empty',
            'has_items',
            'is_subset',
            'is_not_subset',
            'intersects',
            'not_intersects',
            'is_superset',
            'not_superset',
        );
    } else {
        switch (argMetadata.dtype) {
            case 'bool':
            case 'bool|None':
                predicates = pick<CT, keyof CT>(
                    conditionTitles,
                    'dont_care',
                    'is_true',
                    'is_false',
                    'exists',
                    'missing',
                );
                break;
            case 'str':
            case 'EmailStr':
                predicates = pick<CT, keyof CT>(
                    conditionTitles,
                    'dont_care',
                    'exists',
                    'missing',
                    'equals',
                    'not_equals',
                    'is_in',
                    'not_in',
                    'match_regex',
                    'not_match_regex',
                    'length_equals',
                    'length_not_equals',
                    'longer_than',
                    'shorter_than',
                );
                break;
            case 'int':
            case 'float':
                predicates = pick<CT, keyof CT>(
                    conditionTitles,
                    'dont_care',
                    'exists',
                    'missing',
                    'equals',
                    'not_equals',
                    'greater_than',
                    'less_than',
                    'greater_or_equals',
                    'less_or_equals',
                    'in_range',
                    'out_of_range',
                    'is_in',
                    'not_in',
                );
                break;
            case 'Image':
            case 'Url':
            case 'VideoUrl':
            case 'OrderInfo':
                predicates = pick<CT, keyof CT>(conditionTitles, 'dont_care', 'exists', 'missing');
                break;
            case 'datetime':
                predicates = pick<CT, keyof CT>(
                    conditionTitles,
                    'dont_care',
                    'exists',
                    'missing',
                    'days_passed_greater_or_equals',
                    'days_passed_less',
                    'before_date',
                    'after_date',
                );
                break;
            default:
                predicates = conditionTitles;
                break;
        }
    }

    if (isCategoricalMetadata(argMetadata)) {
        predicates = {
            ...predicates,
            ...pick<CT, keyof CT>(
                conditionTitles,
                'dont_care',
                'exists',
                'missing',
                'equals',
                'not_equals',
                'is_in',
                'not_in',
                'is_neither',
            ),
        };
    }

    return predicates;
}

export function getAllowedPredicates(argMetadata: IArgumentMetadata): Partial<CT> {
    const result = getPredicates(argMetadata);
    if (argMetadata.arg_type === 'TEXT_ARGUMENT') {
        result['is_unspecified'] = conditionTitles['is_unspecified'];
        result['is_unknown'] = conditionTitles['is_unknown'];
    }
    return result;
}

export const createCondition = (predicate: Predicate, arg: IArgumentMetadata, condId?: IPolicyCondition['id']) => {
    const newCond = {
        id: condId,
        uiId: newUiId(),
        predicate,
        argument_metadata_id: arg.id,
        argument_metadata: arg,
        value: { special: [] },
        order: 0,
    };

    return isBaseCondition(newCond)
        ? ({
              ...newCond,
              value: { special: [] },
          } as IPolicyConditionWithArgMetadata)
        : ({
              ...newCond,
              value: { value: null, special: [] },
          } as IPolicyConditionWithArgMetadata);
};

export const PredicateButton = <T extends IPolicyCondition>(
    props: PartialBy<ConditionEditorProps<T>, 'condition'> & {
        buttonProps?: ComponentProps<typeof ButtonLikeTypography>;
        variant?: ComponentProps<typeof ButtonLikeTypography>['buttonVariant'];
        disabled?: ComponentProps<typeof ButtonLikeTypography>['disabled'];
        width?: ComponentProps<typeof ButtonLikeTypography>['width'];
        fullWidth?: boolean;
        hasRightNeighbors?: boolean;
        dontCareTitle: string;
    },
) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        if (!props.disabled) {
            setAnchorEl(event.currentTarget);
        }
    };

    const selectNewCondition = useCallback(
        (predicate: PR) => {
            setAnchorEl(null);

            if (predicate === props.condition?.predicate) {
                return;
            }

            if (predicate !== 'dont_care') {
                const conditionBase = createCondition(predicate, props.argumentMetadata, props.condition?.id);
                props.setCondition(conditionBase);
            } else if (props.condition) {
                props.setCondition(undefined);
            }
        },
        [props.argumentMetadata, props.condition, props.setCondition],
    );

    const id = newUiId();
    const menuId = `predicate-menu-${id}`;
    const buttonId = `predicate-button-${id}`;

    const menuItems = useMemo(() => {
        const allowedPredicates = getAllowedPredicates(props.argumentMetadata);
        if (allowedPredicates.dont_care) {
            allowedPredicates.dont_care = props.dontCareTitle;
        }
        return Object.entries(allowedPredicates)
            .sort(([, value1], [, value2]) => value1.localeCompare(value2))
            .map(([key, value]) => (
                <MenuItem key={`${id}-${key}`} onClick={() => selectNewCondition(key as Predicate)}>
                    {value}
                </MenuItem>
            ));
    }, [selectNewCondition, props.argumentMetadata, props.dontCareTitle]);

    return (
        <>
            <ButtonLikeTypography
                id={buttonId}
                aria-controls={open ? menuId : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                buttonVariant={props.variant || 'text'}
                disabled={props.disabled}
                width={props.width}
                fullWidth={props.fullWidth}
                hasRightNeighbors={props.hasRightNeighbors}
            >
                {props.title}
            </ButtonLikeTypography>
            <Menu
                id={menuId}
                aria-labelledby={buttonId}
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{ maxHeight: 550 }}
            >
                {menuItems}
            </Menu>
        </>
    );
};
PredicateButton.displayName = 'PredicateButton';
